import { useEffect, useRef } from "react";
import { a3Copy } from "./a3-copy";
import type { Result } from "@/components/ui/box.schema";
import { assignmentsApi, type Submission } from "@/lib/api/assignments";
import { checkCommit, type CommitReceipt } from "@/lib/api/commitSeams";
import { privateRequest } from "@/lib/private-file-transfer";

export const checkSubmissionCommit = <T,>(key: string) => privateRequest(signal => checkCommit<T>(key, undefined, signal));

/** An exact committed request stays confirmed even while its academic details
 * cannot be read. Retrying this read never reissues the hand-in or Check. */
export function useSubmissionReceipt(handlers: {
  onCommitted: () => void;
  onReceipt: (receipt: Submission) => Result | Promise<Result>;
  onResult: (result: Result) => Result;
}) {
  const latest = useRef(handlers); latest.current = handlers;
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const flight = useRef(false);
  const confirmed = (identity: { id: string; at: string }, loading = false): Result => ({ state: "confirmed", at: identity.at,
    effects: [a3Copy.yourHandInIsRecorded],
    remains: [a3Copy.yourOriginalSubmissionTimeIsPreserved, loading ? a3Copy.readingReceiptDetails : a3Copy.receiptDetailsCouldNotBeRead],
    refresh: { verb: a3Copy.readReceiptDetails, note: a3Copy.thisReadsTheSameHandInItNeverSends, run: () => read(identity) },
  });
  const read = async (identity: { id: string; at: string }): Promise<Result> => {
    if (flight.current) return confirmed(identity, true);
    flight.current = true;
    try {
      const receipt = await privateRequest(signal => assignmentsApi.getSubmission(identity.id, signal));
      if (!live.current) return confirmed(identity);
      const result = await latest.current.onReceipt(receipt);
      return result;
    } catch {
      const result = confirmed(identity);
      return live.current ? latest.current.onResult(result) : result;
    } finally {
      flight.current = false;
    }
  };
  return {
    confirm: async (receipt: CommitReceipt<{ submission_id: string }>): Promise<Result> => {
      const identity = { id: receipt.result.submission_id, at: receipt.recorded_at };
      latest.current.onCommitted();
      latest.current.onResult(confirmed(identity, true));
      return read(identity);
    },
  };
}
